import React from "react";
import { 
  Sparkles, 
  Sun, 
  Frame, 
  TreeDeciduous, 
  Droplets, 
  Home,
  ArrowRight,
  LayoutGrid,
  Car,
  Building2,
  Fence,
  Zap
} from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import ResponsiveImage from "./ResponsiveImage";
import { images } from "../data/images";
import { services, hogedrukServices } from "../data/mock";

const iconMap = {
  Sparkles,
  Sun,
  Frame,
  TreeDeciduous,
  Droplets,
  Home,
  LayoutGrid,
  Car,
  Building2,
  Fence,
};

const ServiceCard = ({ service, index, gradientFrom = "sky", gradientTo = "teal" }) => {
  const IconComponent = iconMap[service.icon];
  const gradientClasses = {
    "sky-teal": "from-sky-500 to-teal-500 shadow-sky-500/20",
    "orange-amber": "from-orange-500 to-amber-500 shadow-orange-500/20",
  };
  const gradient = `${gradientFrom}-${gradientTo}`;
  
  return (
    <Card
      className="group relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-gradient-to-br from-white to-slate-50"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <CardContent className="p-6">
        {/* Icon */}
        <div className={`w-14 h-14 bg-gradient-to-br ${gradientClasses[gradient] || gradientClasses["sky-teal"]} rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
          {IconComponent && (
            <IconComponent className="w-7 h-7 text-white" />
          )}
        </div>

        {/* Content */}
        <h3 className="text-xl font-bold text-slate-800 mb-2 group-hover:text-sky-600 transition-colors">
          {service.title}
        </h3>
        <p className="text-slate-600 mb-4">{service.description}</p>

        {/* Link */}
        <Link
          to={`/${service.slug}`}
          data-testid={`service-card-link-${service.slug}`}
          className="inline-flex items-center text-sky-600 font-semibold group-hover:text-teal-600 transition-colors"
        >
          <span>Meer informatie</span>
          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
        </Link>

        {/* Decorative corner */}
        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-sky-500/10 to-transparent rounded-bl-full" />
      </CardContent>
    </Card>
  );
};

const Services = () => {
  return (
    <section id="diensten" className="py-24 bg-white" aria-labelledby="diensten-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 bg-sky-100 text-sky-700 rounded-full text-sm font-semibold mb-4">
            Onze Diensten
          </span>
          <h2 id="diensten-heading" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-4">
            Wat kunnen wij voor u doen?
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Van glasbewassing tot dak reinigen - wij verzorgen al uw
            schoonmaakwerkzaamheden rondom het huis.
          </p>
        </div>

        {/* Action Photo Banner */}
        <div className="relative rounded-3xl overflow-hidden mb-16 shadow-xl group">
          <ResponsiveImage
            image={images.action}
            alt="Cleven Cleaning glazenwasser in actie - professionele glasbewassing voor woningen en bedrijven in Rosmalen en Den Bosch"
            sizes="(min-width: 1280px) 1280px, 100vw"
            className="w-full h-64 sm:h-80 lg:h-96 object-cover group-hover:scale-105 transition-transform duration-700"
            data-testid="services-action-photo"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 sm:bottom-8 sm:left-8">
            <p className="text-white text-lg sm:text-2xl font-bold drop-shadow-lg">Vakmanschap in actie</p>
            <p className="text-white/80 text-sm sm:text-base drop-shadow-md">Professionele glazenwassing voor elk type gebouw</p>
          </div>
        </div>

        {/* Regular Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {services.map((service, index) => (
            <ServiceCard 
              key={service.id} 
              service={service} 
              index={index}
              gradientFrom="sky"
              gradientTo="teal"
            />
          ))}
        </div>

        {/* Hogedruk Services Section */}
        <div className="relative">
          {/* Section Divider */}
          <div className="flex items-center justify-center mb-12">
            <div className="h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent flex-1" />
            <div className="px-6 flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/20">
                <Zap className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent flex-1" />
          </div>

          {/* Hogedruk Header */}
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold mb-4">
              Hogedruk Reiniging
            </span>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              Hogedruk Werkzaamheden
            </h3>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Met professionele hogedrukapparatuur verwijderen wij hardnekkig vuil, 
              mos en groene aanslag van diverse oppervlakken.
            </p>
          </div>

          {/* Hogedruk Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {hogedrukServices.map((service, index) => (
              <ServiceCard 
                key={`hogedruk-${service.id}`} 
                service={service} 
                index={index}
                gradientFrom="orange"
                gradientTo="amber"
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
